import React, { Key, memo, ReactElement, useCallback, useContext, useMemo } from 'react'

import { Table } from 'antd'
import styled from 'styled-components'
import { v4 as uuidv4 } from 'uuid'

import { EditableCell, EditableRow } from './temporary-data-table/editable-table-utils'
import { wellColumns } from './temporary-data-table/static-data-columns'
import { saveAllTemporaryData } from './importer-api'
import { TemporaryDataType, TemporarySetDataType } from './temporary-data-table/useTemporaryDataState'
import TemporaryInterventionTable from './temporary-data-table/TemporaryInterventionTable'
import { emptyWell } from './utils/emptyEntities'
import { CustomTableHeader } from './temporary-data-table/components/CustomTableHeader'
import { CustomExpandRowIcon } from './temporary-data-table/components/CustomExpandRowIcon'
import { deleteEntityAndItsChildren, deletedCascadeEntities } from './utils/actions'
import { StaticDataImporterContext } from './context/StaticDataImporterContext'
import { useSelectAllRows } from './hooks/useSelectAllRows'
import { useNavigateToNewRow } from './hooks/useNavigateToNewRow'

type EditableTableProps = Parameters<typeof Table>[0]

type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>

interface TemporaryDataTableProps {
    data: TemporaryDataType
    setData: TemporarySetDataType
    localErrors: LocalErrorsTypes[]
    removeErrorWhenCheckedRowIsRemoved: (value: TemporaryDataType) => void
    isErrorsSidebarOpen: boolean
    liveWells: { id: string; name: string }[]
    liveRigs: { id: string; name: string }[]
    optionsOfWellEntity: WellSelectOptions
    handleEditSave: (row: WellDto) => void
}

const TemporaryDataTable = memo(
    ({
        data,
        setData,
        localErrors,
        removeErrorWhenCheckedRowIsRemoved,
        liveWells,
        liveRigs,
        optionsOfWellEntity,
        handleEditSave
    }: TemporaryDataTableProps): JSX.Element => {
        const { selectAllRows } = useSelectAllRows()

        const {
            checkedRows,
            setCheckedRows,
            formatCheckedRows,
            expandedRowKeys,
            setExpandedRowKeysByEntity
        } = useContext(StaticDataImporterContext)

        const { goToNewRow, classNameNewRow } = useNavigateToNewRow()

        const wells = useMemo(
            () =>
                data.wells.map(well => ({
                    ...well,
                    key: String(well.rowId)
                })),
            [data.wells]
        )

        const handleAddNewRow = useCallback((): void => {
            const oldEntity = [...data.wells]

            const newEntity = {
                ...emptyWell,
                parent: Date.now() - 10000,
                nodeId: Date.now(),
                frontUuid: uuidv4()
            } as WellDto

            saveAllTemporaryData(newEntity, 'WELL').then(saves => {
                newEntity.rowId = saves[0].id
                newEntity.key = saves[0].id
                newEntity.newRow = true
                oldEntity.push(newEntity)
                setData.setWells(oldEntity)
                goToNewRow()
            })
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [goToNewRow, setData])

        const handleDelete = useCallback(
            (record: WellDto): void => {
                const newData = data.wells.filter(item => String(item.rowId) !== String(record.rowId))
                const newCheckedRows = deletedCascadeEntities(checkedRows, record)

                deleteEntityAndItsChildren(record.rowId).then(() => {
                    const newCheckedToTransfer = formatCheckedRows(newCheckedRows)

                    setData.setWells(newData)
                    setCheckedRows(newCheckedRows)
                    removeErrorWhenCheckedRowIsRemoved(newCheckedToTransfer)
                })
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [checkedRows, formatCheckedRows, removeErrorWhenCheckedRowIsRemoved, setCheckedRows, setData]
        )

        const wellsOptions = useMemo(() => liveWells.map(opt => ({ label: opt.name, value: opt.id })), [liveWells])

        const rigsOptions = useMemo(() => liveRigs.map(opt => ({ label: opt.name, value: opt.id })), [liveRigs])

        const wellCols = useMemo(
            () =>
                wellColumns(handleDelete, handleEditSave, wellsOptions, rigsOptions, optionsOfWellEntity, localErrors),
            [handleDelete, handleEditSave, wellsOptions, rigsOptions, optionsOfWellEntity, localErrors]
        )

        const components = useMemo(
            () => ({
                body: {
                    row: EditableRow,
                    cell: EditableCell
                }
            }),
            []
        )

        const expandedRowRender = useCallback(
            (record: WellDto): ReactElement => {
                return (
                    <TemporaryInterventionTable
                        parent={record}
                        data={data}
                        setData={setData}
                        localErrors={localErrors}
                        removeErrorWhenCheckedRowIsRemoved={removeErrorWhenCheckedRowIsRemoved}
                    />
                )
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [localErrors, removeErrorWhenCheckedRowIsRemoved, setData]
        )

        const expandIcon = useCallback(({ expanded, onExpand, record }): ReactElement => {
            return <CustomExpandRowIcon expanded={expanded} onExpand={onExpand} record={record as WellDto} />
        }, [])

        const title = useCallback((): ReactElement => {
            return <CustomTableHeader title="wells" handleAddNewRow={handleAddNewRow} />
        }, [handleAddNewRow])

        const rowSelection = useMemo(
            () => ({
                type: 'checkbox' as const,
                onChange: (selectedRowsKey: React.Key[], selectedRows: WellDto[]) => {
                    selectAllRows({
                        data,
                        parentNodeId: selectedRows?.map(row => row.parent),
                        selectedRows,
                        type: 'wells' as const
                    })
                },
                onSelect: (record: EntityDto, selected: boolean, selectedRows: WellDto[], nativeEvent: Event) => {
                    selectAllRows({
                        data,
                        parentNodeId: record?.nodeId,
                        type: 'wells' as const,
                        selectedRows,
                        target: record,
                        selected,
                        nativeEvent
                    })
                },
                checkStrictly: false
            }),
            [data, selectAllRows]
        )

        const handleExpandedRowsChange = useCallback(
            (keys: React.Key[]): void => {
                setExpandedRowKeysByEntity('wells', keys as Key[])
            },
            [setExpandedRowKeysByEntity]
        )

        const expandableConfig = useMemo(
            () => ({
                expandedRowRender,
                expandIcon,
                expandedRowKeys: expandedRowKeys?.wells,
                defaultExpandAllRows: false,
                onExpandedRowsChange: handleExpandedRowsChange
            }),
            [expandedRowRender, expandIcon, expandedRowKeys?.wells, handleExpandedRowsChange]
        )

        return (
            <TableContainer>
                <TableContent>
                    <Table
                        title={title}
                        size="small"
                        components={components}
                        rowClassName={(d: EntityDto) =>
                            d?.newRow ? `editable-row ${classNameNewRow}` : 'editable-row'
                        }
                        bordered
                        dataSource={wells}
                        columns={wellCols as ColumnTypes}
                        pagination={false}
                        rowSelection={rowSelection}
                        expandable={expandableConfig}
                    />
                </TableContent>
            </TableContainer>
        )
    }
)

export default TemporaryDataTable

const TableContainer = styled.main`
    width: 100%;
    height: calc(100vh - 209px);
    overflow-y: auto;

    border-bottom: 1px solid var(--gray-400);

    .ant-table-title {
        width: 100% !important;
        padding: 8px;
        border-radius: 3px 3px 0 0;

        color: var(--gray-0);
        background-color: var(--gray-900);
    }

    .ant-table-container {
        table {
            min-width: 100%;

            thead,
            tbody {
                tr {
                    height: 40px;

                    background-color: var(--gray-0);
                }
            }

            thead {
                text-transform: uppercase;
                font-style: normal;
                font-size: 10px;
                line-height: 13px;
                user-select: none;

                tr {
                    th {
                        vertical-align: center;
                        text-align: left;
                        padding: 6px 4px 6px 8px;
                        height: inherit;

                        border-top: 1px solid var(--gray-300);
                        border-left: 1px solid var(--gray-300);
                        border-right: 1px solid var(--gray-300);

                        background-color: var(--gray-200);
                    }
                }
            }

            tbody {
                min-width: 100%;

                tr:not(thead tr) {
                    border: 1px solid var(--gray-300);
                    vertical-align: center;

                    td {
                        vertical-align: center;
                        text-align: left;
                        height: inherit;
                        font-style: normal;
                        font-size: 13px;
                        line-height: 13px;

                        border-left: 1px solid var(--gray-300);
                        border-right: 1px solid var(--gray-300);
                        &:first-child {
                            border-left: none;
                        }

                        &:last-child {
                            border-right: none;
                        }

                        div.disabledField,
                        div.td-content.num,
                        div.td-content.text,
                        div:has(> [class='editable-cell-value-wrap']):not(div.td-content.calendar, div.td-content.select) {
                            display: flex;
                            align-items: center;
                            justify-content: flex-start;
                            height: 100%;
                            width: 100%;

                            div {
                                display: flex;
                                align-items: center;
                                justify-content: flex-start;
                                height: 100%;
                                width: 100%;
                            }
                        }

                        div.td-content.action,
                        div:has(> [class^='CustomExpandRowIcon']),
                        .td-content:has(> input[type='checkbox']:first-child),
                        .td-content:has(> label span input[type='checkbox']:first-child),
                        div:has(> [class='ant-checkbox-wrapper']) {
                            display: flex;
                            align-items: center;
                            justify-content: left;
                            height: 100%;
                            width: 100%;

                            div {
                                display: flex;
                                align-items: center;
                                justify-content: left;
                                height: 100%;
                                width: 100%;
                            }
                        }
                    }

                    div.td-content.select {
                        min-width: 150px;
                    }

                    div.td-content {
                        justify-content: left;
                    }

                    &.active-row {
                        position: relative;
                        z-index: 2;
                        background-color: var(--blue-200);
                    }

                    &.active-error {
                        border: 1px solid var(--red-500);
                        background-color: var(--red-200);
                    }

                    &.editable-row:hover {
                        background-color: var(--blue-200);
                    }
                }
            }
        }

        .ant-table-row-expand-icon-cell {
            width: 45px !important;
            div:is(.td-content) {
                width: 45px !important;
            }
        }

        .ant-table-selection-column {
            width: 32px !important;

            div:is(.td-content) {
                width: 32px !important;
                padding: 6px;
            }
        }

        .ant-table-expanded-row {
            .td-content:first-child {
                width: 100%;
            }
        }

        .td-content {
            width: 150px;
            height: 100%;
            padding: 6px;

            &.disabledField {
                width: 70px !important;
                opacity: 0.6;
                cursor: default;
                background-color: var(--gray-100);
            }

            &.action {
                width: 70px !important;
            }

            &.num {
                width: 100px !important;
            }

            &.calendar {
                width: 150px !important;
            }

            // select
            .form-group {
                margin: 0;

                .Select-value {
                    .Select-value-label {
                        div {
                            overflow: hidden;
                            text-overflow: ellipsis;
                        }
                    }
                }
            }

            .editable-cell-value-wrap {
                cursor: pointer;
                height: 100%;

                margin-right: 8px;
            }

            .ant-form-item {
                margin: 0;
                margin-right: 8px;

                .ant-input-sm {
                    height: 36px;
                }
            }
        }
    }

    .ant-table-container {
        .ant-table-tbody {
            .ant-table-cell {
                .move-btn {
                    position: relative;
                    outline: none;
                    border: none;
                    background-color: transparent;
                }
                .move-modal {
                    position: absolute;
                    right: 48px;
                    width: 230px;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    padding: 16px;
                    z-index: 99999;
                    border: 1px solid var(--gray-300);

                    background-color: var(--gray-100);
                    box-shadow: 2px 4px 4px rgba(0, 0, 0, 0.25);
                }
            }
        }
    }

    .editable-cell {
        position: relative;
    }

    // Changing scrollbar css
    /* width */
    ::-webkit-scrollbar {
        width: 8px;
    }

    /* outside */
    ::-webkit-scrollbar-track {
        background: var(--gray-100);
    }

    /* Handle */
    ::-webkit-scrollbar-thumb {
        border-radius: 8px;
        background: var(--gray-500);
    }
`

const TableContent = styled.div`
    .new-row:not(:focus) {
        td {
            background: var(--gray-200) !important;
        }
    }
`
